import { useEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from '@/styles/theme';
import { EngineProvider } from './EngineContext';
import { createEngine, type Engine } from './engine';
import { SpatialCanvas } from '@/components/canvas/SpatialCanvas';
import { Toolbar, StatusBar, Inspector } from '@/components/chrome';
import { Launcher } from '@/components/launcher/Launcher';
import { Dock } from '@/components/dock/Dock';
import { CommandPalette } from '@/components/palette/CommandPalette';
import { DeveloperPanel } from '@/components/chrome/DeveloperPanel';
import { WorkspaceDialogs } from '@/components/chrome/WorkspaceDialogs';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { bindPersistence } from '@/features/persistence/bindPersistence';
import {
  createPersistenceService,
  createLocalStorageAdapter,
  createMemoryStorageAdapter,
} from '@/services/persistence';
import { env } from '@/config/env';
import { STORAGE_KEYS } from '@/constants/storage';

const Shell = () => {
  useKeyboardShortcuts();
  return (
    <>
      <SpatialCanvas />
      <Toolbar />
      <Inspector />
      <Dock />
      <StatusBar />
      <Launcher />
      <CommandPalette />
      <DeveloperPanel />
      <WorkspaceDialogs />
    </>
  );
};

/**
 * Composition root. The engine and persistence binding are created once per mount
 * and torn down on unmount so hot reloads and tests never leak subscriptions.
 */
export const App = () => {
  const [engine] = useState<Engine>(createEngine);

  useEffect(() => {
    const adapter =
      env.persistenceEnabled && 'localStorage' in globalThis
        ? createLocalStorageAdapter()
        : createMemoryStorageAdapter();
    let seedShowcase = env.persistenceEnabled;
    try {
      if (globalThis.localStorage?.getItem(STORAGE_KEYS.seed) === 'off') seedShowcase = false;
    } catch {
      seedShowcase = false;
    }
    const unbindPersistence = bindPersistence(createPersistenceService(adapter), { seedShowcase });
    engine.start();
    return () => {
      engine.stop();
      unbindPersistence();
    };
  }, [engine]);

  return (
    <ThemeProvider theme={theme}>
      <EngineProvider engine={engine}>
        <Shell />
      </EngineProvider>
    </ThemeProvider>
  );
};
